import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronUp, ChevronDown, Mail, User, Banknote, Sparkles, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../lib/api';

/**
 * Premium first-session onboarding checklist for new organisers.
 *
 * Lives at the top of the OrganiserDashboard. Auto-hides once the
 * organiser has verified email + created their first event, OR if
 * they explicitly dismiss it. We persist the dismiss flag in
 * localStorage (per-browser) so it doesn't badger them forever.
 */
const STORAGE_KEY = 'vp_onboarding_dismissed_v1';

const Step = ({ done, locked, icon: Icon, title, description, action }) => (
  <div
    data-testid={`onboarding-step-${done ? 'done' : (locked ? 'locked' : 'todo')}`}
    className={`flex items-start gap-4 py-4 ${locked ? 'opacity-60' : ''}`}
  >
    <div className="flex-shrink-0 mt-1">
      {done ? (
        <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={2} />
      ) : (
        <Circle className={`w-5 h-5 ${locked ? 'text-stone-300' : 'text-[#1F3D2B]'}`} strokeWidth={1.75} />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#1F3D2B]" />
        <p className={`font-serif text-base ${done ? 'text-stone-400 line-through' : 'text-[#1F3D2B]'}`}>
          {title}
        </p>
        {locked && (
          <span className="text-[10px] uppercase tracking-wider text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">
            Coming soon
          </span>
        )}
      </div>
      <p className={`text-sm mt-1 ${done ? 'text-stone-400' : 'text-stone-600'}`}>{description}</p>
      {!done && !locked && action && <div className="mt-2">{action}</div>}
    </div>
  </div>
);

const OnboardingChecklist = ({ user, hasFirstEvent, hasProfile, hasBanking, onResendVerification, onSetupProfile, onAddBanking, resending }) => {
  const initialDismissed = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1';
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(initialDismissed);

  if (dismissed) return null;
  if (user?.role !== 'organiser') return null;
  if (user?.email_verified && hasProfile && hasBanking && hasFirstEvent) return null; // auto-hide when fully ready

  const verified = !!user?.email_verified;
  const profileSet = !!hasProfile;
  const bankingSet = !!hasBanking;
  const completed = [verified, profileSet, bankingSet, hasFirstEvent].filter(Boolean).length;
  const total = 4; // verify + profile + banking + first event
  const progressPct = Math.round((completed / total) * 100);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      data-testid="onboarding-checklist"
      className="mb-8 bg-gradient-to-br from-[#FBF9F4] to-white border border-[#E5D9B6] rounded-2xl shadow-sm overflow-hidden"
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-[#1F3D2B] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#C9A27A]" />
          </div>
          <div className="min-w-0">
            <p className="font-serif text-lg text-[#1F3D2B] truncate">Welcome to VinePass, {user?.name?.split(' ')[0]}.</p>
            <p className="text-xs text-stone-500">Get ready to sell — {completed} of {total} steps complete</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Progress ring (mini) */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-32 h-1.5 bg-stone-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1F3D2B] transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-xs text-stone-500 tabular-nums">{progressPct}%</span>
          </div>
          {collapsed ? <ChevronDown className="w-5 h-5 text-stone-500" /> : <ChevronUp className="w-5 h-5 text-stone-500" />}
        </div>
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="px-6 pb-6 pt-0">
          <div className="border-t border-[#E5D9B6]/50 divide-y divide-stone-100">
            <Step
              done={true}
              icon={CheckCircle2}
              title="Account created"
              description="Your VinePass organiser account is live."
            />
            <Step
              done={verified}
              icon={Mail}
              title="Verify your email"
              description={verified
                ? "You're verified — event creation and payouts are unlocked."
                : "Click the link we emailed you to unlock event creation."}
              action={
                <button
                  type="button"
                  onClick={onResendVerification}
                  disabled={resending}
                  data-testid="onboarding-resend-verification"
                  className="text-xs underline text-[#1F3D2B] hover:no-underline disabled:opacity-50"
                >
                  {resending ? 'Sending…' : 'Resend verification email'}
                </button>
              }
            />
            <Step
              done={profileSet}
              icon={User}
              title="Set up your organiser profile"
              description={profileSet ? "Your organiser profile is live." : "Add your venue name, logo, and a short bio for your event pages."}
              action={
                <button
                  type="button"
                  onClick={onSetupProfile}
                  data-testid="onboarding-setup-profile"
                  className="text-xs underline text-[#1F3D2B] hover:no-underline"
                >
                  {profileSet ? 'Edit profile' : 'Set up profile →'}
                </button>
              }
            />
            <Step
              done={bankingSet}
              icon={Banknote}
              title="Add banking details"
              description={bankingSet
                ? "Banking details saved securely — you can request payouts after your event."
                : "Required before you can withdraw funds from completed events."}
              action={
                <button
                  type="button"
                  onClick={onAddBanking}
                  data-testid="onboarding-add-banking"
                  className="text-xs underline text-[#1F3D2B] hover:no-underline"
                >
                  {bankingSet ? 'Update banking →' : 'Add banking details →'}
                </button>
              }
            />
            <Step
              done={hasFirstEvent}
              icon={Sparkles}
              title="Create your first event"
              description={hasFirstEvent
                ? "Your first experience is live — well done."
                : "List your first wine experience and start selling tickets."}
              action={
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!verified) {
                      toast.info('Please verify your email first.');
                      return;
                    }
                    document.querySelector('[data-testid="create-event-button"]')?.click();
                  }}
                  className="text-xs underline text-[#1F3D2B] hover:no-underline"
                  data-testid="onboarding-create-event"
                >
                  Create an event →
                </Link>
              }
            />
          </div>

          <div className="mt-5 flex items-center justify-between text-xs text-stone-400">
            <span>You can dismiss this checklist anytime.</span>
            <button
              type="button"
              onClick={handleDismiss}
              data-testid="onboarding-dismiss"
              className="inline-flex items-center gap-1 hover:text-stone-600"
            >
              <X className="w-3 h-3" />
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
